import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Edit, BookOpen, DollarSign, Loader2, Copy, Trash2, GraduationCap, Utensils, Languages, FileText } from 'lucide-react';
import { canEdit } from '@/lib/auth';
import api from '@/lib/api';
import { ReplicateFinancialButton, ReplicateFinancialDialog, SegmentBulkValuesButton, CourseSyncBadge } from './CoursesFinancialReplication';

const PLAN_CATEGORIES = {
  mensalidade: { label: 'Mensalidade Escolar', icon: GraduationCap, color: 'bg-blue-100 text-blue-700' },
  material: { label: 'Material Didático', icon: FileText, color: 'bg-purple-100 text-purple-700' },
  matricula: { label: 'Taxa de Matrícula', icon: DollarSign, color: 'bg-green-100 text-green-700' },
  bilingue: { label: 'Programa Bilíngue', icon: Languages, color: 'bg-orange-100 text-orange-700' },
  integral_almoco: { label: 'Período Integral c/ Almoço', icon: Utensils, color: 'bg-red-100 text-red-700' },
  integral_sem_almoco: { label: 'Período Integral s/ Almoço', icon: Utensils, color: 'bg-amber-100 text-amber-700' }
};

export default function Courses() {
  const [courses, setCourses] = useState([]);
  // V9.13 · Ano letivo é filtro-mestre da página (cursos, turmas e planos nunca se misturam)
  const [year, setYear] = useState(() => {
    const saved = localStorage.getItem('courses_year');
    if (saved === 'none') return 'none';
    return Number(saved) || new Date().getFullYear();
  });
  const [years, setYears] = useState([]);
  const [overview, setOverview] = useState(null);
  const [syncCourseId, setSyncCourseId] = useState(null);
  const [financialPlans, setFinancialPlans] = useState([]);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('dados');
  const [activePlanCategory, setActivePlanCategory] = useState('mensalidade');
  
  const [formData, setFormData] = useState({
    name: '', acronym: '', course_type: '', equivalent_series: '',
    directors: '', coordinator: '', num_modules: 1, num_vacancies: '',
    break_even_point: '', min_age: '', min_enrollments: '', max_dependencies: '',
    max_completion_time: '', requires_fundamental: false, requires_medio: false,
    requires_superior: false, modality: 'Presencial', active: true, notes: ''
  });

  const [planFormData, setPlanFormData] = useState({
    course_id: '', name: '', category: 'mensalidade', description: '',
    num_installments: 12, installment_value: '', start_date: '', due_day: 10,
    scholarship_id: '', abatement: 0, first_installment_different_value: false,
    first_installment_value: '', first_installment_different_date: false,
    first_installment_date: '', use_enrollment_date: false, use_current_month: false,
    is_default: false, active: true
  });

  const courseTypes = ['Educação Infantil', 'Ensino Fundamental', 'Ensino Médio'];
  const modalities = ['Presencial', 'Semi-presencial', 'A distância'];

  useEffect(() => { loadData(); }, []);

  useEffect(() => { localStorage.setItem('courses_year', String(year)); loadOverview(); }, [year]);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadOverview = async () => {
    if (year === 'none') { setOverview(null); return; }
    try {
      const { data } = await api.get('/financial/replication/overview', { params: { year } });
      setOverview(data);
    } catch (error) { setOverview(null); }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [coursesRes, plansRes, scholarshipsRes, yearsRes] = await Promise.all([
        api.get('/courses'),
        api.get('/course-financial-plans'),
        api.get('/scholarships'),
        api.get('/financial/replication/years').catch(() => ({ data: { years: [] } })),
      ]);
      setCourses(coursesRes.data);
      setFinancialPlans(plansRes.data);
      setScholarships(scholarshipsRes.data);
      setYears(yearsRes.data.years || []);
      loadOverview();
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', acronym: '', course_type: '', equivalent_series: '',
      directors: '', coordinator: '', num_modules: 1, num_vacancies: '',
      break_even_point: '', min_age: '', min_enrollments: '', max_dependencies: '',
      max_completion_time: '', requires_fundamental: false, requires_medio: false,
      requires_superior: false, modality: 'Presencial', active: true, notes: ''
    });
    setEditing(null);
    setActiveTab('dados');
  };

  const resetPlanForm = () => {
    setPlanFormData({
      course_id: selectedCourse?.id || '', name: '', category: activePlanCategory, description: '',
      num_installments: 12, installment_value: '', start_date: '', due_day: 10,
      scholarship_id: '', abatement: 0, first_installment_different_value: false,
      first_installment_value: '', first_installment_different_date: false,
      first_installment_date: '', use_enrollment_date: false, use_current_month: false,
      is_default: false, active: true
    });
    setEditingPlan(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.acronym || !formData.course_type) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    try {
      const data = {
        ...formData,
        num_modules: parseInt(formData.num_modules) || 1,
        num_vacancies: formData.num_vacancies ? parseInt(formData.num_vacancies) : null,
        min_age: formData.min_age ? parseInt(formData.min_age) : null,
        min_enrollments: formData.min_enrollments ? parseInt(formData.min_enrollments) : null,
      };
      if (editing) {
        await api.put(`/courses/${editing.id}`, data);
        toast.success('Curso atualizado!');
      } else {
        await api.post('/courses', data);
        toast.success('Curso criado!');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar curso');
    }
  };

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    if (!planFormData.name || !planFormData.installment_value) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    if (!planFormData.start_date && !planFormData.first_installment_date) {
      toast.error('Informe a Data Inicial (1º vencimento) — sem ela o vencimento dos títulos seria adivinhado.');
      return;
    }
    try {
      const data = {
        ...planFormData,
        course_id: selectedCourse.id,
        category: activePlanCategory,
        installment_value: parseFloat(planFormData.installment_value) || 0,
        num_installments: parseInt(planFormData.num_installments) || 12,
        due_day: parseInt(planFormData.due_day) || 10,
        abatement: parseFloat(planFormData.abatement) || 0,
        first_installment_value: planFormData.first_installment_value ? parseFloat(planFormData.first_installment_value) : null,
        scholarship_id: planFormData.scholarship_id || null
      };
      if (editingPlan) {
        await api.put(`/course-financial-plans/${editingPlan.id}`, data);
        toast.success('Plano atualizado!');
      } else {
        await api.post('/course-financial-plans', data);
        toast.success('Plano criado!');
      }
      setPlanDialogOpen(false);
      resetPlanForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar plano');
    }
  };

  const handleEdit = (course) => {
    setEditing(course);
    setFormData({ ...course });
    setDialogOpen(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanFormData({ ...plan, installment_value: plan.installment_value?.toString() || '' });
    setPlanDialogOpen(true);
  };

  const handleReplicatePlan = async (planId) => {
    try {
      await api.post(`/course-financial-plans/${planId}/replicate`);
      toast.success('Plano replicado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao replicar plano');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Tem certeza que deseja excluir este plano?')) return;
    try {
      await api.delete(`/course-financial-plans/${planId}`);
      toast.success('Plano excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir plano');
    }
  };

  const openCourseDetail = (course) => {
    setSelectedCourse(course);
    setEditing(course);
    setFormData({ ...course });
    setActiveTab('dados');
    setDialogOpen(true);
  };

  const planYear = (p) => {
    const y = p.year || p.auto_year;
    if (y) return Number(y);
    const m = String(p.name || p.description || '').match(/\b(20\d{2})\b/);
    return m ? Number(m[1]) : null;
  };

  const coursesWithoutYear = courses.filter(c => !c.year);

  const filteredCourses = courses.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.acronym.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'all' || c.course_type === filterType;
    const matchesYear = year === 'none' ? !c.year : Number(c.year) === Number(year);
    return matchesSearch && matchesType && matchesYear;
  });

  const overviewMap = Object.fromEntries((overview?.courses || []).map(c => [c.id, c]));

  const getPlansForCategory = (category) => {
    return financialPlans.filter(p => p.course_id === selectedCourse?.id && p.category === category
      && (planYear(p) === null || planYear(p) === Number(year)));
  };

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  return (
    <div className="p-6 space-y-6" data-testid="courses-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-4xl font-bold tracking-tight">Cursos</h1>
          <p className="text-muted-foreground mt-1">Gerenciar cursos e planos financeiros por categoria</p>
        </div>
        {canEdit() && (
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <div className="flex items-center gap-2">
            <Label className="text-xs font-bold uppercase text-muted-foreground">Ano letivo</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(v === 'none' ? 'none' : Number(v))}>
              <SelectTrigger className="w-40 h-9" data-testid="courses-year-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(years.length ? years : [year]).map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                {coursesWithoutYear.length > 0 && (
                  <SelectItem value="none">Sem ano ({coursesWithoutYear.length})</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          {year !== 'none' && <ReplicateFinancialButton year={year} overview={overview} onDone={loadData} />}
          {year !== 'none' && <SegmentBulkValuesButton year={year} overview={overview} onDone={loadData} />}
          <NextYearCoursesButton onGenerated={loadData} />
          <DraftFinancialPlansButton plans={financialPlans} courses={courses} onChanged={loadData} />
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { resetForm(); setSelectedCourse(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-course-button" onClick={() => { resetForm(); setSelectedCourse(null); }}>
                <Plus className="w-4 h-4 mr-2" /> Novo Curso
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? `Editar: ${editing.name}` : 'Novo Curso'}</DialogTitle>
                <DialogDescription>Configure os dados e planos financeiros do curso</DialogDescription>
              </DialogHeader>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="dados">Dados</TabsTrigger>
                  <TabsTrigger value="requisitos">Requisitos</TabsTrigger>
                  <TabsTrigger value="financeiro" disabled={!editing}>Financeiro</TabsTrigger>
                  <TabsTrigger value="bolsas" data-testid="tab-bolsas">Bolsas</TabsTrigger>
                </TabsList>

                <TabsContent value="dados">
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Nome do Curso *</Label>
                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Sigla *</Label>
                        <Input value={formData.acronym} onChange={(e) => setFormData({...formData, acronym: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Tipo do Curso *</Label>
                        <Select value={formData.course_type || undefined} onValueChange={(v) => setFormData({...formData, course_type: v})}>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {courseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Série Equivalente</Label>
                        <Input value={formData.equivalent_series} onChange={(e) => setFormData({...formData, equivalent_series: e.target.value})} />
                      </div>
                      <div>
                        <Label>Coordenador</Label>
                        <Input value={formData.coordinator} onChange={(e) => setFormData({...formData, coordinator: e.target.value})} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={formData.active} onCheckedChange={(c) => setFormData({...formData, active: c})} />
                      <Label>Ativo</Label>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit">{editing ? 'Salvar' : 'Criar'}</Button>
                    </DialogFooter>
                  </form>
                </TabsContent>

                <TabsContent value="requisitos">
                  <div className="space-y-4 mt-4">
                    <p className="text-sm font-medium">Requisitos para o curso:</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.requires_fundamental} onCheckedChange={(c) => setFormData({...formData, requires_fundamental: c})} />
                        <Label>Ensino Fundamental Completo</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.requires_medio} onCheckedChange={(c) => setFormData({...formData, requires_medio: c})} />
                        <Label>Ensino Médio Completo</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.requires_superior} onCheckedChange={(c) => setFormData({...formData, requires_superior: c})} />
                        <Label>Ensino Superior Completo</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button onClick={handleSubmit}>{editing ? 'Salvar' : 'Criar'}</Button>
                    </DialogFooter>
                  </div>
                </TabsContent>

                <TabsContent value="financeiro">
                  <div className="space-y-4 mt-4">
                    {/* Abas de Categorias */}
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(PLAN_CATEGORIES).map(([key, cat]) => {
                        const Icon = cat.icon;
                        const plans = getPlansForCategory(key);
                        return (
                          <Button
                            key={key}
                            variant={activePlanCategory === key ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActivePlanCategory(key)}
                            className="gap-2"
                          >
                            <Icon className="w-4 h-4" />
                            {cat.label}
                            {plans.length > 0 && <Badge variant="secondary" className="ml-1">{plans.length}</Badge>}
                          </Button>
                        );
                      })}
                    </div>

                    {/* Lista de Planos da Categoria */}
                    <Card>
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-base">{PLAN_CATEGORIES[activePlanCategory]?.label}</CardTitle>
                          <Button size="sm" onClick={() => { resetPlanForm(); setPlanDialogOpen(true); }}>
                            <Plus className="w-4 h-4 mr-1" /> Incluir Plano
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {getPlansForCategory(activePlanCategory).length === 0 ? (
                          <div className="text-center py-6 text-muted-foreground">
                            <p>Nenhum plano cadastrado para esta categoria</p>
                          </div>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Nome</TableHead>
                                <TableHead>Parcelas</TableHead>
                                <TableHead>Valor</TableHead>
                                <TableHead>Vencimento</TableHead>
                                <TableHead>Ações</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {getPlansForCategory(activePlanCategory).map(plan => (
                                <React.Fragment key={plan.id}>
                                <TableRow>
                                  <TableCell className="font-medium">
                                    {plan.name}
                                    {plan.is_default && <Badge className="ml-2" variant="outline">Padrão</Badge>}
                                  </TableCell>
                                  <TableCell>
                                    {plan.num_installments}x
                                    {plan.num_installments > 1 && (
                                      <Button size="sm" variant="ghost" className="ml-2 h-6 text-xs"
                                        onClick={() => setExpandedPlanId(expandedPlanId === plan.id ? null : plan.id)}
                                        data-testid={`btn-toggle-schedule-${plan.id}`}>
                                        {expandedPlanId === plan.id ? 'Recolher' : `Ver ${plan.num_installments} parcelas`}
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell>{formatCurrency(plan.installment_value)}</TableCell>
                                  <TableCell>Dia {plan.due_day}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      <Button variant="ghost" size="icon" onClick={() => handleEditPlan(plan)}><Edit className="w-4 h-4" /></Button>
                                      <Button variant="ghost" size="icon" onClick={() => handleReplicatePlan(plan.id)}><Copy className="w-4 h-4" /></Button>
                                      <Button variant="ghost" size="icon" onClick={() => handleDeletePlan(plan.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {expandedPlanId === plan.id && (
                                  <TableRow>
                                    <TableCell colSpan={5} className="bg-purple-50/40 p-4">
                                      <PlanScheduleEditor plan={plan} onSaved={loadData} />
                                    </TableCell>
                                  </TableRow>
                                )}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="bolsas">
                  <ScholarshipsManager scholarships={scholarships} onChanged={loadData} />
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
        )}
      </div>

      {/* Filtros */}
      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por nome ou sigla..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                {courseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Cursos */}
      {coursesWithoutYear.length > 0 && year !== 'none' && (
        <p className="text-xs text-amber-700" data-testid="courses-without-year-warning">
          {coursesWithoutYear.length} curso(s) sem ano letivo definido não aparecem no filtro de {year} — veja em “Sem ano”.
        </p>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : filteredCourses.length === 0 ? (
        <Card className="border-border/60">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nenhum curso encontrado em {year}</p>
            {coursesWithoutYear.length > 0 && (
              <p className="text-xs text-amber-700 mt-2" data-testid="courses-without-year">
                {coursesWithoutYear.length} curso(s) sem ano letivo definido não aparecem neste filtro.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCourses.map(course => {
            const ov = overviewMap[course.id];
            const plans = ov ? ov.plans : financialPlans.filter(p => p.course_id === course.id && planYear(p) === Number(year));
            const categoriesWithPlans = [...new Set(plans.map(p => p.category))];
            const classGroups = course.class_groups || [];
            const totalStudents = course.total_students || 0;
            
            return (
              <Card key={course.id} className="border-border/60 hover:border-primary/50 transition-colors cursor-pointer" onClick={() => openCourseDetail(course)}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <Badge variant={course.active ? 'default' : 'secondary'}>{course.active ? 'Ativo' : 'Inativo'}</Badge>
                    <span className="text-xs text-muted-foreground font-mono">{course.acronym}</span>
                  </div>
                  <CardTitle className="text-lg">{course.name}</CardTitle>
                  <CardDescription>{course.course_type}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Turmas vinculadas */}
                  {classGroups.length > 0 && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium flex items-center gap-1">
                          <GraduationCap className="w-4 h-4" />
                          Turmas Vinculadas
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {classGroups.length} turma{classGroups.length > 1 ? 's' : ''} • {totalStudents} aluno{totalStudents !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {classGroups.slice(0, 6).map(turma => (
                          <Badge key={turma.id} variant="outline" className="text-xs bg-background">
                            {turma.code || turma.name.split(' - ')[0]}
                            {turma.shift && <span className="ml-1 text-muted-foreground">({turma.shift.charAt(0)})</span>}
                          </Badge>
                        ))}
                        {classGroups.length > 6 && (
                          <Badge variant="outline" className="text-xs">+{classGroups.length - 6}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Planos financeiros */}
                  <div className="flex flex-wrap gap-1">
                    {categoriesWithPlans.map(cat => {
                      const catInfo = PLAN_CATEGORIES[cat];
                      return catInfo ? (
                        <Badge key={cat} className={catInfo.color} variant="outline">{catInfo.label}</Badge>
                      ) : null;
                    })}
                  </div>
                  <p className="text-sm text-muted-foreground">{plans.length} plano(s) financeiro(s)</p>
                  {ov && <CourseSyncBadge course={ov} onSync={(id) => setSyncCourseId(id)} />}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog de Plano Financeiro */}
      <ReplicateFinancialDialog
        year={year} overview={overview} presetCourseId={syncCourseId}
        open={!!syncCourseId} onOpenChange={(v) => { if (!v) setSyncCourseId(null); }}
        onDone={loadData}
      />
      <Dialog open={planDialogOpen} onOpenChange={(open) => { setPlanDialogOpen(open); if (!open) resetPlanForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Editar Plano' : `Novo Plano - ${PLAN_CATEGORIES[activePlanCategory]?.label}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePlanSubmit} className="space-y-4">
            <div>
              <Label>Nome do Plano *</Label>
              <Input value={planFormData.name} onChange={(e) => setPlanFormData({...planFormData, name: e.target.value})} placeholder={`${PLAN_CATEGORIES[activePlanCategory]?.label} - ${selectedCourse?.name}`} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nº de Parcelas *</Label>
                <Input type="number" value={planFormData.num_installments} onChange={(e) => setPlanFormData({...planFormData, num_installments: e.target.value})} required />
              </div>
              <div>
                <Label>Valor da Parcela *</Label>
                <Input type="number" step="0.01" value={planFormData.installment_value} onChange={(e) => setPlanFormData({...planFormData, installment_value: e.target.value})} required />
              </div>
              <div>
                <Label>Data Inicial (1º vencimento) *</Label>
                <Input type="date" value={planFormData.start_date || ''} required
                  data-testid="plan-start-date"
                  onChange={(e) => setPlanFormData({...planFormData, start_date: e.target.value})} />
                <p className="text-[11px] text-muted-foreground mt-1">
                  O ano desta data manda no vencimento dos títulos (nunca o ano corrente).
                </p>
              </div>
              <div>
                <Label>Dia Vencimento</Label>
                <Input type="number" min="1" max="28" value={planFormData.due_day} onChange={(e) => setPlanFormData({...planFormData, due_day: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Bolsa/Desconto</Label>
              <Select value={planFormData.scholarship_id || 'none'} onValueChange={(v) => setPlanFormData({...planFormData, scholarship_id: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Sem desconto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem desconto</SelectItem>
                  {scholarships.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.discount_type === 'fixed' ? `R$ ${Number(s.discount_value || 0).toFixed(2)}` : `${Number(s.discount_value || s.discount_percent || 0)}%`})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Abatimento (R$)</Label>
              <Input type="number" step="0.01" value={planFormData.abatement} onChange={(e) => setPlanFormData({...planFormData, abatement: e.target.value})} />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Opções 1ª Parcela</p>
              <div className="flex items-center gap-2">
                <Checkbox checked={planFormData.first_installment_different_value} onCheckedChange={(c) => setPlanFormData({...planFormData, first_installment_different_value: c})} />
                <Label>Valor Diferenciado</Label>
                {planFormData.first_installment_different_value && (
                  <Input type="number" step="0.01" className="w-32 ml-2" value={planFormData.first_installment_value || ''} onChange={(e) => setPlanFormData({...planFormData, first_installment_value: e.target.value})} placeholder="Valor" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={planFormData.first_installment_different_date} onCheckedChange={(c) => setPlanFormData({...planFormData, first_installment_different_date: c})} />
                <Label>Data Diferenciada</Label>
                {planFormData.first_installment_different_date && (
                  <Input type="date" className="w-40 ml-2" value={planFormData.first_installment_date || ''} onChange={(e) => setPlanFormData({...planFormData, first_installment_date: e.target.value})} />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={planFormData.is_default} onCheckedChange={(c) => setPlanFormData({...planFormData, is_default: c})} />
              <Label>Plano padrão para esta categoria</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPlanDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">{editingPlan ? 'Salvar' : 'Criar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CoursesStatBox({ label, value }) {
  return (
    <div className="border rounded p-2 bg-purple-50/40">
      <div className="text-xl font-bold">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function NextYearCoursesButton({ onGenerated }) {
  const [open, setOpen] = useState(false);
  const [srcYear, setSrcYear] = useState(String(new Date().getFullYear()));
  const [cloneFin, setCloneFin] = useState(false);
  const [copyValues, setCopyValues] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [overrides, setOverrides] = useState({});

  const reset = () => { setPreview(null); setResult(null); setOverrides({}); setCloneFin(false); };

  const doPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/courses/next-year/preview', { source_year: Number(srcYear) });
      setPreview(data);
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  };

  const doGenerate = async () => {
    setLoading(true);
    try {
      const payload = {
        source_year: Number(srcYear),
        clone_financial_plans_as_draft: cloneFin,
        copy_plan_values: copyValues,
        overrides: Object.entries(overrides).map(([source_id, o]) => ({ source_id, ...o })),
      };
      const { data } = await api.post('/courses/next-year/generate', payload);
      setResult(data);
      toast.success(`${data.created} cursos criados para ${data.target_year}.`);
      onGenerated && onGenerated();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error('Erro: ' + (typeof det === 'string' ? det : (det?.detail || err.message)));
    }
    finally { setLoading(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="btn-next-year-courses"
        className="border-purple-300 text-purple-700"
      >
        ✨ Gerar Próximo Ano
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dialog-next-year-courses">
          <DialogHeader>
            <DialogTitle>Preparar cursos para {preview?.target_year || Number(srcYear) + 1}</DialogTitle>
            <DialogDescription>
              Vamos clonar a estrutura acadêmica dos cursos de {srcYear} para {Number(srcYear) + 1}. Nenhum aluno, matrícula, nota ou título será copiado.
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="space-y-3" data-testid="courses-generation-result">
              <div className="text-center py-4">
                <div className="text-4xl">✨</div>
                <div className="text-lg font-medium mt-2">Cursos de {result.target_year} preparados</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="border rounded p-3 bg-green-50">
                  <div className="text-2xl font-bold text-green-700">{result.created}</div>
                  <div className="text-xs">Cursos criados</div>
                </div>
                <div className="border rounded p-3 bg-purple-50">
                  <div className="text-2xl font-bold">{result.already_existed}</div>
                  <div className="text-xs">Já existentes</div>
                </div>
                <div className="border rounded p-3 bg-gray-50">
                  <div className="text-2xl font-bold">{(result.skipped || []).length}</div>
                  <div className="text-xs">Pulados</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">Batch id: <code>{result.batch_id}</code></div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
              </div>
            </div>
          ) : preview ? (
            <div className="space-y-3" data-testid="courses-generation-preview">
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <CoursesStatBox label="Analisados" value={preview.analyzed} />
                <CoursesStatBox label="Serão criados" value={preview.will_create} />
                <CoursesStatBox label="Já existem" value={preview.already_exists} />
              </div>
              <div className="border rounded max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0"><tr>
                    <th className="text-left p-2">Curso {preview.source_year}</th>
                    <th className="text-left p-2">Proposta {preview.target_year}</th>
                    <th className="text-left p-2">Sigla</th>
                    <th className="p-2">Pular</th>
                    <th className="p-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {preview.courses.map((c) => (
                      <tr key={c.source_id} className="border-t">
                        <td className="p-2">{c.source_name}</td>
                        <td className="p-2">{c.proposed_name}</td>
                        <td className="p-2 text-muted-foreground">{c.proposed_acronym || '—'}</td>
                        <td className="p-2 text-center">
                          <Checkbox
                            checked={!!overrides[c.source_id]?.skip}
                            onCheckedChange={(v) => setOverrides({ ...overrides, [c.source_id]: { ...(overrides[c.source_id] || {}), skip: !!v } })}
                            disabled={c.status === 'exists'}
                          />
                        </td>
                        <td className="p-2 text-center">
                          {c.status === 'exists' ? (
                            <Badge variant="outline" className="bg-blue-100">✓ Já existe</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-100">Criar</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={cloneFin} onCheckedChange={(v) => setCloneFin(!!v)} data-testid="chk-clone-financial" />
                <Label className="text-xs">Clonar planos financeiros como rascunho (draft)</Label>
              </div>
              {cloneFin && (
                <div className="flex items-center gap-2 pl-6">
                  <Checkbox checked={copyValues} onCheckedChange={(v) => setCopyValues(!!v)} data-testid="chk-copy-plan-values" />
                  <Label className="text-xs">
                    Copiar também os valores de {srcYear} (desmarque para clonar só a estrutura:
                    parcelas, mês inicial e vencimento — valores entram zerados para edição em massa)
                  </Label>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setPreview(null)}>Voltar</Button>
                <Button onClick={doGenerate} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-confirm-generate-courses">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Confirmar e gerar {preview.target_year}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ano de origem</Label>
                  <Input value={srcYear} onChange={(e) => setSrcYear(e.target.value)} type="number" data-testid="courses-next-year-src" />
                </div>
                <div>
                  <Label className="text-xs">Ano de destino</Label>
                  <Input value={Number(srcYear) + 1} disabled />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={doPreview} disabled={loading} data-testid="btn-preview-next-year-courses">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Visualizar prévia
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


// V6.5.2 · Editor de Planos Financeiros em Rascunho (do curso do ano destino)
function DraftFinancialPlansButton({ plans, courses, onChanged }) {
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState({}); // {plan_id: {installment_value, num_installments, due_day}}
  const [saving, setSaving] = useState(false);
  const [activatingId, setActivatingId] = useState(null);

  const drafts = (plans || []).filter((p) => (p.status || '').toLowerCase() === 'draft');
  const courseMap = Object.fromEntries((courses || []).map((c) => [c.id, c]));

  const getVal = (p, k, dflt) => {
    const e = edits[p.id] || {};
    return e[k] !== undefined ? e[k] : (p[k] ?? dflt);
  };
  const setVal = (p, k, v) => setEdits((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), [k]: v } }));

  const saveOne = async (p) => {
    setSaving(true);
    try {
      const body = {
        course_id: p.course_id, name: p.name, category: p.category,
        description: p.description || null,
        num_installments: Number(getVal(p, 'num_installments', 12)),
        installment_value: Number(getVal(p, 'installment_value', 0)),
        due_day: Number(getVal(p, 'due_day', 10)),
        start_date: getVal(p, 'start_date', '') || null,
        abatement: p.abatement || 0, is_default: !!p.is_default, active: p.active !== false,
      };
      await api.put(`/course-financial-plans/${p.id}`, body);
      toast.success('Rascunho atualizado.');
      onChanged && onChanged();
      setEdits((prev) => { const { [p.id]: _, ...rest } = prev; return rest; });
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const activate = async (p) => {
    if (!getVal(p, 'start_date', '')) {
      toast.error('Informe a data inicial (1º vencimento) antes de ativar este plano.');
      return;
    }
    if (edits[p.id]) await saveOne(p);
    setActivatingId(p.id);
    try {
      await api.post(`/course-financial-plans/${p.id}/activate`);
      toast.success(`Plano ativado: ${p.name}`);
      onChanged && onChanged();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setActivatingId(null); }
  };

  const activateAll = async () => {
    const semData = drafts.filter((p) => !getVal(p, 'start_date', ''));
    if (semData.length) {
      toast.error(`${semData.length} plano(s) sem data inicial: informe a data antes de ativar.`);
      return;
    }
    setSaving(true);
    try {
      for (const p of drafts) {
        if (edits[p.id]) await saveOne(p);
        await api.post(`/course-financial-plans/${p.id}/activate`);
      }
      toast.success(`${drafts.length} plano(s) ativados.`);
      onChanged && onChanged();
      setEdits({});
    } catch (err) { toast.error('Erro em massa: ' + (err?.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="btn-open-drafts"
        className="border-amber-300 text-amber-700"
        disabled={drafts.length === 0}
        title={drafts.length === 0 ? 'Nenhum plano em rascunho' : ''}
      >
        📝 Rascunhos financeiros ({drafts.length})
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdits({}); }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="dialog-drafts">
          <DialogHeader>
            <DialogTitle>Planos financeiros em rascunho</DialogTitle>
            <DialogDescription>
              Edite os valores clonados do próximo ano antes de ativar. Enquanto estiverem em rascunho, o motor de matrículas ignora esses valores.
            </DialogDescription>
          </DialogHeader>

          {drafts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Sem rascunhos.</div>
          ) : (
            <>
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="bg-muted"><tr>
                    <th className="text-left p-2">Curso</th>
                    <th className="text-left p-2">Categoria</th>
                    <th className="p-2">Ano</th>
                    <th className="p-2">Valor parcela</th>
                    <th className="p-2">Nº parcelas</th>
                    <th className="p-2">Data inicial *</th>
                    <th className="p-2">Vencimento</th>
                    <th className="p-2 text-right">Ações</th>
                  </tr></thead>
                  <tbody>
                    {drafts.map((p) => {
                      const course = courseMap[p.course_id];
                      const dirty = !!edits[p.id];
                      return (
                        <tr key={p.id} className="border-t">
                          <td className="p-2">
                            <div className="font-medium">{course?.name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{course?.acronym}</div>
                          </td>
                          <td className="p-2">
                            <Badge variant="outline" className={PLAN_CATEGORIES[p.category]?.color}>
                              {PLAN_CATEGORIES[p.category]?.label || p.category}
                            </Badge>
                          </td>
                          <td className="p-2 text-center">{p.year || p.auto_year || '—'}</td>
                          <td className="p-2">
                            <Input type="number" step="0.01" className="w-28 h-8"
                              value={getVal(p, 'installment_value', 0)}
                              onChange={(e) => setVal(p, 'installment_value', e.target.value)}
                              data-testid={`draft-value-${p.id}`} />
                          </td>
                          <td className="p-2">
                            <Input type="number" className="w-20 h-8"
                              value={getVal(p, 'num_installments', 12)}
                              onChange={(e) => setVal(p, 'num_installments', e.target.value)} />
                          </td>
                          <td className="p-2">
                            <Input type="date" className="w-36 h-8"
                              value={getVal(p, 'start_date', '') || ''}
                              onChange={(e) => setVal(p, 'start_date', e.target.value)}
                              data-testid={`draft-start-date-${p.id}`} />
                          </td>
                          <td className="p-2">
                            <Input type="number" min="1" max="28" className="w-16 h-8"
                              value={getVal(p, 'due_day', 10)}
                              onChange={(e) => setVal(p, 'due_day', e.target.value)} />
                          </td>
                          <td className="p-2 text-right space-x-1 whitespace-nowrap">
                            {dirty && (
                              <Button size="sm" variant="outline" disabled={saving} onClick={() => saveOne(p)}>
                                Salvar
                              </Button>
                            )}
                            <Button size="sm" className="bg-green-600 hover:bg-green-700"
                              disabled={saving || activatingId === p.id || !getVal(p, 'start_date', '')}
                              title={!getVal(p, 'start_date', '') ? 'Informe a data inicial para ativar' : ''}
                              onClick={() => activate(p)} data-testid={`activate-${p.id}`}>
                              {activatingId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Ativar'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setOpen(false); setEdits({}); }}>Fechar</Button>
                <Button className="bg-green-600 hover:bg-green-700" disabled={saving || drafts.length === 0}
                  onClick={activateAll} data-testid="btn-activate-all-drafts">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Ativar todos ({drafts.length})
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// V6.10 · Gestão completa de Bolsas (criar / editar / desativar / excluir)
const BLANK_SCH = {
  name: '', discount_type: 'percent', discount_value: '', categories: [],
  valid_from: '', valid_to: '', priority: 0, cumulative: false, active: true, description: '',
};

function ScholarshipsManager({ scholarships, onChanged }) {
  const [form, setForm] = useState(BLANK_SCH);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name || '',
      discount_type: s.discount_type || 'percent',
      discount_value: s.discount_value ?? s.discount_percent ?? '',
      categories: s.categories || [],
      valid_from: s.valid_from || '', valid_to: s.valid_to || '',
      priority: s.priority || 0, cumulative: !!s.cumulative,
      active: s.active !== false, description: s.description || '',
    });
  };

  const toggleCat = (key) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(key) ? f.categories.filter((c) => c !== key) : [...f.categories, key],
  }));

  const save = async () => {
    if (!form.name || !(Number(form.discount_value) > 0)) {
      toast.error('Informe nome e valor da bolsa');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value: Number(form.discount_value),
        discount_percent: form.discount_type === 'percent' ? Number(form.discount_value) : 0,
        priority: Number(form.priority) || 0,
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
      };
      if (editingId) await api.put(`/scholarships/${editingId}`, payload);
      else await api.post('/scholarships', payload);
      toast.success(editingId ? 'Bolsa atualizada' : 'Bolsa criada');
      setForm(BLANK_SCH); setEditingId(null);
      onChanged && onChanged();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remover/desativar a bolsa "${s.name}"?`)) return;
    try {
      const { data } = await api.delete(`/scholarships/${s.id}`);
      toast.success(data.message || 'Bolsa removida');
      onChanged && onChanged();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/scholarships/${s.id}`, {
        name: s.name, discount_type: s.discount_type || 'percent',
        discount_value: s.discount_value ?? s.discount_percent ?? 0,
        discount_percent: s.discount_percent ?? 0,
        categories: s.categories || [], valid_from: s.valid_from || null, valid_to: s.valid_to || null,
        priority: s.priority || 0, cumulative: !!s.cumulative, description: s.description || '',
        active: !(s.active !== false),
      });
      onChanged && onChanged();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="scholarships-manager">
      <div className="border rounded p-3 bg-muted/20 space-y-3">
        <div className="text-sm font-medium">{editingId ? 'Editar bolsa' : 'Nova bolsa'}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Nome *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex.: Bolsa 20%, Funcionário, Irmão" data-testid="sch-name" />
          </div>
          <div>
            <Label className="text-xs">Tipo *</Label>
            <select className="w-full border rounded h-10 px-2 text-sm bg-white" value={form.discount_type}
              onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="sch-type">
              <option value="percent">Percentual (%)</option>
              <option value="fixed">Valor fixo (R$)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">{form.discount_type === 'fixed' ? 'Valor (R$) *' : 'Percentual (%) *'}</Label>
            <Input type="number" step="0.01" value={form.discount_value}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="sch-value" />
          </div>
          <div>
            <Label className="text-xs">Vigência início</Label>
            <Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="sch-from" />
          </div>
          <div>
            <Label className="text-xs">Vigência fim</Label>
            <Input type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} data-testid="sch-to" />
          </div>
          <div>
            <Label className="text-xs">Prioridade</Label>
            <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="sch-priority" />
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={form.cumulative} onCheckedChange={(c) => setForm({ ...form, cumulative: !!c })} data-testid="sch-cumulative" />
              Acumulável
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: !!c })} data-testid="sch-active" />
              Ativa
            </label>
          </div>
        </div>
        <div>
          <Label className="text-xs">Categorias às quais se aplica (vazio = todas)</Label>
          <div className="flex flex-wrap gap-3 mt-1">
            {Object.entries(PLAN_CATEGORIES).map(([key, cat]) => (
              <label key={key} className="flex items-center gap-1 text-xs">
                <Checkbox checked={form.categories.includes(key)} onCheckedChange={() => toggleCat(key)}
                  data-testid={`sch-cat-${key}`} />
                {cat.label}
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid="sch-save">
            {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} {editingId ? 'Salvar bolsa' : 'Criar bolsa'}
          </Button>
          {editingId && (
            <Button size="sm" variant="outline" onClick={() => { setForm(BLANK_SCH); setEditingId(null); }} data-testid="sch-cancel">
              Cancelar
            </Button>
          )}
        </div>
      </div>

      {scholarships.length === 0 ? (
        <p className="text-center py-4 text-muted-foreground">Nenhuma bolsa cadastrada</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Desconto</TableHead>
              <TableHead>Categorias</TableHead>
              <TableHead>Vigência</TableHead>
              <TableHead>Prior.</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scholarships.map((s) => (
              <TableRow key={s.id} data-testid={`sch-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>
                  {s.discount_type === 'fixed'
                    ? `R$ ${Number(s.discount_value || 0).toFixed(2)}`
                    : `${Number(s.discount_value || s.discount_percent || 0)}%`}
                  {s.cumulative && <Badge variant="outline" className="ml-2 text-[10px]">acumulável</Badge>}
                </TableCell>
                <TableCell className="text-xs">{(s.categories || []).length ? (s.categories || []).map((c) => PLAN_CATEGORIES[c]?.label || c).join(', ') : 'Todas'}</TableCell>
                <TableCell className="text-xs">{s.valid_from || '—'} → {s.valid_to || '—'}</TableCell>
                <TableCell className="text-xs">{s.priority || 0}</TableCell>
                <TableCell>
                  <Badge className={s.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>
                    {s.active !== false ? 'Ativa' : 'Inativa'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`sch-edit-${s.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-xs" onClick={() => toggleActive(s)} data-testid={`sch-toggle-${s.id}`}>
                      {s.active !== false ? 'Desativar' : 'Ativar'}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`sch-del-${s.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// V6.8 · Editor detalhado de parcelas do plano do curso
function PlanScheduleEditor({ plan, onSaved }) {
  const [rows, setRows] = useState([]);
  const [orig, setOrig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ from_installment: 1, to_installment: '', new_due_day: '', new_amount: '', percent_adjustment: '' });
  const [linkedTitles, setLinkedTitles] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/course-financial-plans/${plan.id}/schedule`);
      setRows(data.schedule || []);
      setOrig(JSON.parse(JSON.stringify(data.schedule || [])));
    } catch (err) { toast.error('Falha ao carregar parcelas'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [plan.id]);

  const update = (idx, k, v) => setRows((r) => r.map((row, i) => i === idx ? { ...row, [k]: v } : row));
  const restore = (idx) => setRows((r) => r.map((row, i) => i === idx ? { ...(orig[idx] || row) } : row));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/course-financial-plans/${plan.id}/schedule`, {
        schedule: rows.map((r) => ({ installment_number: Number(r.installment_number), due_date: r.due_date, amount: Number(r.amount) })),
      });
      setLinkedTitles(data.titles_generated || 0);
      toast.success(`Schedule salvo (${rows.length} parcelas)` + (data.titles_generated ? ` · ${data.titles_generated} títulos já vinculados` : ''));
      setOrig(JSON.parse(JSON.stringify(rows)));
      onSaved && onSaved();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const applyBulk = async () => {
    const payload = { from_installment: Number(bulk.from_installment) };
    if (bulk.to_installment !== '') payload.to_installment = Number(bulk.to_installment);
    if (bulk.new_due_day !== '') payload.new_due_day = Number(bulk.new_due_day);
    if (bulk.new_amount !== '') payload.new_amount = Number(bulk.new_amount);
    if (bulk.percent_adjustment !== '') payload.percent_adjustment = Number(bulk.percent_adjustment);
    try {
      const { data } = await api.post(`/course-financial-plans/${plan.id}/schedule/bulk-edit`, payload);
      setRows(data.schedule || []);
      setOrig(JSON.parse(JSON.stringify(data.schedule || [])));
      setBulkOpen(false);
      toast.success('Edição em massa aplicada e salva.');
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const dirty = JSON.stringify(rows) !== JSON.stringify(orig);

  if (loading) return <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;

  return (
    <div className="space-y-2" data-testid={`schedule-editor-${plan.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {rows.length} parcelas · Total <b>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(total)}</b>
          {linkedTitles > 0 && <span className="ml-2 text-amber-600">· {linkedTitles} títulos vinculados</span>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(!bulkOpen)} data-testid={`btn-bulk-${plan.id}`}>Editar em massa</Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-purple-600 hover:bg-purple-700" data-testid={`btn-save-schedule-${plan.id}`}>
            {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Salvar
          </Button>
        </div>
      </div>

      {bulkOpen && (
        <div className="border rounded p-2 bg-white grid grid-cols-6 gap-2 items-end text-xs" data-testid={`bulk-panel-${plan.id}`}>
          <div><Label className="text-[10px]">De #</Label><Input type="number" className="h-8" value={bulk.from_installment} onChange={(e) => setBulk({ ...bulk, from_installment: e.target.value })} /></div>
          <div><Label className="text-[10px]">Até #</Label><Input type="number" className="h-8" placeholder="fim" value={bulk.to_installment} onChange={(e) => setBulk({ ...bulk, to_installment: e.target.value })} /></div>
          <div><Label className="text-[10px]">Novo dia venc.</Label><Input type="number" className="h-8" min="1" max="28" value={bulk.new_due_day} onChange={(e) => setBulk({ ...bulk, new_due_day: e.target.value })} /></div>
          <div><Label className="text-[10px]">Novo valor</Label><Input type="number" step="0.01" className="h-8" value={bulk.new_amount} onChange={(e) => setBulk({ ...bulk, new_amount: e.target.value })} /></div>
          <div><Label className="text-[10px]">Reajuste %</Label><Input type="number" step="0.01" className="h-8" value={bulk.percent_adjustment} onChange={(e) => setBulk({ ...bulk, percent_adjustment: e.target.value })} /></div>
          <Button size="sm" onClick={applyBulk} className="h-8" data-testid={`btn-bulk-apply-${plan.id}`}>Aplicar</Button>
        </div>
      )}

      <div className="border rounded bg-white max-h-72 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0"><tr>
            <th className="text-left p-2 w-24">Parcela</th>
            <th className="text-left p-2 w-40">Vencimento</th>
            <th className="text-left p-2 w-32">Valor (R$)</th>
            <th className="p-2 w-16"></th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="p-2 text-xs">{r.installment_number}/{rows.length}</td>
                <td className="p-2">
                  <Input type="date" className="h-8" value={r.due_date} onChange={(e) => update(i, 'due_date', e.target.value)} data-testid={`sched-date-${plan.id}-${r.installment_number}`} />
                </td>
                <td className="p-2">
                  <Input type="number" step="0.01" className="h-8" value={r.amount} onChange={(e) => update(i, 'amount', e.target.value)} data-testid={`sched-amount-${plan.id}-${r.installment_number}`} />
                </td>
                <td className="p-2 text-center">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => restore(i)} title="Restaurar">↺</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

